import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ETIQUETAS_ESTADO_LICITACION,
  ETIQUETAS_MOTIVO_PERDIDA,
  ETIQUETAS_PRIORIDAD,
  ETIQUETAS_TIPO_ORGANISMO,
  ETIQUETAS_TIPO_PROCESO,
  MOTIVOS_PERDIDA,
  PRIORIDADES,
  TIPOS_ORGANISMO,
  TIPOS_PROCESO,
  calcularDesdeNeto,
  calcularDesdeTotal,
  formatearCLP,
  formatearPorcentajeSimple,
  type CrearLicitacionInput,
  type EstadoLicitacion,
  type FiltrosPipeline,
  type ModalidadMonto,
  type MotivoPerdida,
  type Prioridad,
  type TarjetaKanban,
  type TipoOrganismo,
  type TipoProceso,
  type UrgenciaLicitacion,
} from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoBuscar,
  IconoCerebro,
  IconoCerrar,
  IconoCopiar,
  IconoMas,
  IconoMotorIA,
  IconoRayo,
  IconoRestablecer,
} from '../componentes/Iconos';
import { CurvaProbabilidad } from '../componentes/CurvaProbabilidad';
import {
  Cargando,
  ErrorCarga,
  Panel,
  TarjetaKpi,
} from '../componentes/UI';
import { useAuth } from '../contextos/AuthContext';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useCategoriasComercialesConfig,
  useCompetidoresCatalogo,
  useOrganismos,
  usePipeline,
  usePrediccion,
  useUsuariosEmpresa,
  type EntradaPrediccion,
} from '../lib/consultas';
import {
  useAdjudicarLicitacionDirecta,
  useCambiarEstadoLicitacion,
  useCrearLicitacionRapida,
  useEliminarLicitacion,
} from '../lib/mutaciones';

// ── Colores y Estilos por Columna ──────────────────────────────
const COLORES_ESTADO: Record<string, string> = {
  OPORTUNIDAD: 'var(--accent-blue)',
  EN_PREPARACION: 'var(--accent-purple)',
  OFERTA_ENVIADA: 'var(--accent-yellow)',
  ADJUDICADA: 'var(--accent-green)',
  PERDIDA: 'var(--accent-red)',
};

const BORDES_COLUMNA: Record<string, string> = {
  OPORTUNIDAD: 'rgba(59, 130, 246, 0.25)',
  EN_PREPARACION: 'rgba(139, 92, 246, 0.25)',
  OFERTA_ENVIADA: 'rgba(245, 158, 11, 0.25)',
  ADJUDICADA: 'rgba(16, 185, 129, 0.35)',
  PERDIDA: 'rgba(239, 68, 68, 0.25)',
};

function formatearFechaHora(fecha: string | null | undefined): string {
  if (!fecha) return '—';
  const f = new Date(fecha);
  return isNaN(f.getTime())
    ? '—'
    : `${f.toLocaleDateString('es-CL')} ${f.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`;
}

function obtenerBadgeUrgencia(urgencia: UrgenciaLicitacion, diasRestantes: number | null) {
  switch (urgencia) {
    case 'URGENTE_HOY':
      return {
        texto: '🔥 ¡Cierra Hoy!',
        fondo: 'rgba(239, 68, 68, 0.15)',
        color: 'var(--accent-red)',
        borde: '1px solid rgba(239, 68, 68, 0.4)',
      };
    case 'PROXIMO':
      return {
        texto: `⏰ Cierra en ${diasRestantes ?? 0}d`,
        fondo: 'rgba(245, 158, 11, 0.15)',
        color: 'var(--accent-yellow)',
        borde: '1px solid rgba(245, 158, 11, 0.35)',
      };
    case 'NORMAL':
      return {
        texto: `📅 ${diasRestantes ?? 0}d restantes`,
        fondo: 'var(--bg-surface)',
        color: 'var(--text-secondary)',
        borde: '1px solid var(--border-subtle)',
      };
    case 'FINALIZADA':
      return {
        texto: '✓ Finalizada',
        fondo: 'rgba(16, 185, 129, 0.12)',
        color: 'var(--accent-green)',
        borde: '1px solid rgba(16, 185, 129, 0.25)',
      };
    case 'SIN_FECHA':
    default:
      return {
        texto: 'Sin fecha límite',
        fondo: 'transparent',
        color: 'var(--text-muted)',
        borde: '1px dashed var(--border-subtle)',
      };
  }
}

export function Pipeline() {
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const avisos = useAvisos();
  const esGestorOAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER' || usuario?.rol === 'GESTOR';

  // ── Filtros reactivos en tiempo real ──────────────────────────────
  const [busqueda, setBusqueda] = useState('');
  const [sectorFiltro, setSectorFiltro] = useState<TipoOrganismo | ''>('');
  const [categoriaIdFiltro, setCategoriaIdFiltro] = useState('');
  const [responsableIdFiltro, setResponsableIdFiltro] = useState('');
  const [soloVencimientoProximo, setSoloVencimientoProximo] = useState(false);

  // ── Drag & Drop state ─────────────────────────────────────────────
  const [arrastrandoId, setArrastrandoId] = useState<string | null>(null);
  const [columnaHover, setColumnaHover] = useState<string | null>(null);

  // ── Modales ───────────────────────────────────────────────────────
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [tarjetaParaAdjudicar, setTarjetaParaAdjudicar] = useState<TarjetaKanban | null>(null);
  const [tarjetaParaPerder, setTarjetaParaPerder] = useState<TarjetaKanban | null>(null);
  const [tarjetaParaDetalle, setTarjetaParaDetalle] = useState<TarjetaKanban | null>(null);
  const [tarjetaParaEliminar, setTarjetaParaEliminar] = useState<TarjetaKanban | null>(null);
  const [menuMoverAbiertoId, setMenuMoverAbiertoId] = useState<string | null>(null);

  // ── Ergonomía del Tablero Kanban & Vista Lista ────────────────────
  const [vistaActual, setVistaActual] = useState<'KANBAN' | 'TABLA'>('KANBAN');
  const [etapaFiltroMuro, setEtapaFiltroMuro] = useState<string>('TODAS');
  const [densidad, setDensidad] = useState<'COMODA' | 'COMPACTA'>('COMODA');
  const [paginaPorColumna, setPaginaPorColumna] = useState<Record<string, number>>({});

  const cambiarPaginaColumna = (estado: string, delta: number, totalPaginas: number) => {
    setPaginaPorColumna((prev) => {
      const actual = prev[estado] || 1;
      const nueva = Math.min(totalPaginas, Math.max(1, actual + delta));
      return { ...prev, [estado]: nueva };
    });
  };

  // ── Catálogos auxiliares para filtros ─────────────────────────────
  const categoriasQuery = useCategoriasComercialesConfig();
  const usuariosQuery = useUsuariosEmpresa();

  // ── Consulta principal de Pipeline ────────────────────────────────
  const filtrosQuery: FiltrosPipeline = useMemo(
    () => ({
      busqueda: busqueda.trim() || undefined,
      sector: sectorFiltro || undefined,
      categoriaId: categoriaIdFiltro || undefined,
      responsableId: responsableIdFiltro || undefined,
      soloVencimientoProximo: soloVencimientoProximo ? true : undefined,
    }),
    [busqueda, sectorFiltro, categoriaIdFiltro, responsableIdFiltro, soloVencimientoProximo],
  );

  const { data, isLoading, isError, error, refetch } = usePipeline(filtrosQuery);

  // ── Mutaciones ────────────────────────────────────────────────────
  const mutacionCambiarEstado = useCambiarEstadoLicitacion();

  const hayFiltrosActivos =
    busqueda.trim() !== '' ||
    sectorFiltro !== '' ||
    categoriaIdFiltro !== '' ||
    responsableIdFiltro !== '' ||
    soloVencimientoProximo;

  const resetearFiltros = () => {
    setBusqueda('');
    setSectorFiltro('');
    setCategoriaIdFiltro('');
    setResponsableIdFiltro('');
    setSoloVencimientoProximo(false);
  };

  // ── Lógica de arrastre y soltado ──────────────────────────────────
  const manejarInicioArrastre = (e: React.DragEvent<HTMLDivElement>, tarjeta: TarjetaKanban) => {
    if (!esGestorOAdmin) return;
    setArrastrandoId(tarjeta.id);
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: tarjeta.id, estadoActual: tarjeta.urgencia }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const manejarFinArrastre = () => {
    setArrastrandoId(null);
    setColumnaHover(null);
  };

  const manejarArrastrarSobreColumna = (e: React.DragEvent<HTMLDivElement>, estadoColumna: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (columnaHover !== estadoColumna) {
      setColumnaHover(estadoColumna);
    }
  };

  const manejarSoltarEnColumna = (estadoDestino: string) => {
    setColumnaHover(null);
    if (!arrastrandoId || !data) return;

    // Buscar la tarjeta arrastrada
    let tarjetaEncontrada: TarjetaKanban | null = null;
    let columnaOrigen: string | null = null;

    for (const col of data.columnas) {
      const match = col.tarjetas.find((t) => t.id === arrastrandoId);
      if (match) {
        tarjetaEncontrada = match;
        columnaOrigen = col.estado;
        break;
      }
    }

    if (!tarjetaEncontrada || columnaOrigen === estadoDestino) {
      setArrastrandoId(null);
      return;
    }

    procesarCambioDeEtapa(tarjetaEncontrada, estadoDestino as EstadoLicitacion);
    setArrastrandoId(null);
  };

  const procesarCambioDeEtapa = (tarjeta: TarjetaKanban, nuevoEstado: EstadoLicitacion) => {
    if (nuevoEstado === 'ADJUDICADA') {
      setTarjetaParaAdjudicar(tarjeta);
      return;
    }

    if (nuevoEstado === 'PERDIDA') {
      setTarjetaParaPerder(tarjeta);
      return;
    }

    // Para OPORTUNIDAD, EN_PREPARACION, OFERTA_ENVIADA
    mutacionCambiarEstado.mutate(
      {
        id: tarjeta.id,
        datos: { estado: nuevoEstado },
      },
      {
        onSuccess: () => {
          avisos.exito(`Oferta movida a ${ETIQUETAS_ESTADO_LICITACION[nuevoEstado]} exitosamente`);
        },
        onError: (err: any) => {
          const mensaje = err?.mensaje || err?.message || 'No se pudo cambiar de estado';
          avisos.error(mensaje);
        },
      },
    );
  };

  const todasLasOfertas = useMemo(() => {
    if (!data?.columnas) return [];
    const lista: (TarjetaKanban & { estadoColumna: string; tituloColumna: string })[] = [];
    for (const col of data.columnas) {
      for (const t of col.tarjetas) {
        lista.push({ ...t, estadoColumna: col.estado, tituloColumna: col.titulo });
      }
    }
    return lista;
  }, [data?.columnas]);

  if (isLoading) return <Cargando texto="Cargando tablero comercial y métricas del pipeline…" />;
  if (isError) return <ErrorCarga error={error} reintentar={() => void refetch()} />;
  if (!data) return null;

  const { kpis, columnas } = data;

  return (
    <>
      {/* ── 1. Cabecera Ejecutiva ─────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
            Pipeline & Gestión de Licitaciones
          </h1>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Centro de control del embudo comercial en compras públicas. Monitorea ofertas en juego, cierres inminentes
            y transfiere directamente a ejecución operacional al adjudicar.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Acceso directo al Motor Predictivo */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navegar('/motor')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title="Abrir panel de diagnóstico y calibración del Motor Predictivo IA"
          >
            <IconoMotorIA size={14} />
            <span>Motor Predictivo IA</span>
          </button>

          <button
            type="button"
            className={`btn ${soloVencimientoProximo ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setSoloVencimientoProximo((prev) => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            title="Mostrar solo ofertas que cierran en los próximos 7 días"
          >
            <IconoRayo size={14} />
            <span>Cierran en ≤ 7 días</span>
            {kpis.cierresProximos48h > 0 && (
              <span
                style={{
                  background: 'var(--accent-red)',
                  color: '#fff',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  marginLeft: 4,
                }}
              >
                {kpis.cierresProximos48h}
              </span>
            )}
          </button>

          {esGestorOAdmin && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setModalCrearAbierto(true)}
              title="Registrar una nueva licitación en 30 segundos con sugerencia del Motor Predictivo"
            >
              <IconoMas size={15} />
              <span>+ Nueva Oferta (30 seg)</span>
            </button>
          )}
        </div>
      </div>

      {/* ── 2. Primer Pliegue: 4 KPIs Ejecutivos ────────────────────── */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <TarjetaKpi
          etiqueta="En Juego (Pipeline Abierto)"
          valor={formatearCLP(kpis.enJuegoNeto)}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota="Monto neto activo en postulación o evaluación"
        />

        <TarjetaKpi
          etiqueta="Adjudicado Mes en Curso"
          valor={formatearCLP(kpis.adjudicadoMesNeto)}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="Monto adjudicado en el mes calendario"
        />

        <TarjetaKpi
          etiqueta="Win Rate del Pipeline"
          valor={`${kpis.winRatePct}%`}
          acento={kpis.winRatePct >= 40 ? 'var(--accent-green)' : kpis.winRatePct >= 20 ? 'var(--accent-yellow)' : 'var(--accent-red)'}
          colorValor={kpis.winRatePct >= 40 ? 'var(--accent-green)' : kpis.winRatePct >= 20 ? 'var(--accent-yellow)' : 'var(--accent-red)'}
          nota="Tasa de éxito sobre procesos resueltos"
        />

        <TarjetaKpi
          etiqueta="Cierres Próximas 48h"
          valor={String(kpis.cierresProximos48h)}
          acento={kpis.cierresProximos48h > 0 ? 'var(--accent-red)' : 'var(--accent-gray)'}
          colorValor={kpis.cierresProximos48h > 0 ? 'var(--accent-red)' : 'var(--text-primary)'}
          nota={kpis.cierresProximos48h > 0 ? '¡Atención inmediata requerida!' : 'Sin cierres urgentes en 48 horas'}
        />
      </div>

      {/* ── 3. Barra de Búsqueda y Filtros 100% Reactivos ──────────── */}
      <Panel style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Búsqueda de texto */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              placeholder="Buscar por código MP, nombre u organismo…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ paddingLeft: 32, width: '100%' }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                }}
              >
                <IconoCerrar size={14} />
              </button>
            )}
          </div>

          {/* Filtro Sector */}
          <div style={{ minWidth: 170 }}>
            <select
              value={sectorFiltro}
              onChange={(e) => setSectorFiltro(e.target.value as TipoOrganismo | '')}
              style={{ width: '100%' }}
            >
              <option value="">🏛️ Todos los Sectores</option>
              {TIPOS_ORGANISMO.map((sec) => (
                <option key={sec} value={sec}>
                  {ETIQUETAS_TIPO_ORGANISMO[sec]}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Categoría Comercial */}
          <div style={{ minWidth: 180 }}>
            <select
              value={categoriaIdFiltro}
              onChange={(e) => setCategoriaIdFiltro(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">📁 Todas las Líneas de Negocio</option>
              {categoriasQuery.data?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Responsable */}
          <div style={{ minWidth: 170 }}>
            <select
              value={responsableIdFiltro}
              onChange={(e) => setResponsableIdFiltro(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">👤 Todos los Responsables</option>
              {usuariosQuery.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Botón Restablecer */}
          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={resetearFiltros}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <IconoRestablecer size={13} />
              <span>Limpiar filtros</span>
            </button>
          )}

          {/* Conmutador de Vista: Tablero Kanban vs Lista/Tabla */}
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: 4,
              background: 'var(--bg-surface)',
              padding: 3,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <button
              type="button"
              className={`btn btn-sm ${vistaActual === 'KANBAN' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                padding: '3px 10px',
                fontSize: 11.5,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontWeight: vistaActual === 'KANBAN' ? 700 : 500,
              }}
              onClick={() => setVistaActual('KANBAN')}
              title="Ver como tablero Kanban con arrastrar y soltar"
            >
              <span>▦</span>
              <span>Kanban</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm ${vistaActual === 'TABLA' ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                padding: '3px 10px',
                fontSize: 11.5,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontWeight: vistaActual === 'TABLA' ? 700 : 500,
              }}
              onClick={() => setVistaActual('TABLA')}
              title="Ver como lista / tabla ejecutiva para auditoría de alto volumen"
            >
              <span>☷</span>
              <span>Lista / Tabla ({todasLasOfertas.length})</span>
            </button>
          </div>
        </div>
      </Panel>

      {/* ── 4. Vista Dual: Tablero Kanban Ergonómico o Lista/Tabla ──── */}
      {vistaActual === 'KANBAN' ? (
        <>
          {/* Barra Superior de Pestañas de Etapa y Densidad */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginBottom: 18,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            {/* Píldoras de Acceso Rápido y Filtrado por Etapa */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`btn btn-sm ${etapaFiltroMuro === 'TODAS' ? 'btn-primary' : 'btn-secondary'}`}
                style={{
                  borderRadius: 20,
                  padding: '4px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
                onClick={() => setEtapaFiltroMuro('TODAS')}
              >
                🌟 Todas las Etapas ({todasLasOfertas.length})
              </button>

              {columnas.map((col) => {
                const color = COLORES_ESTADO[col.estado] || 'var(--accent-blue)';
                const esActivo = etapaFiltroMuro === col.estado;
                return (
                  <button
                    key={col.estado}
                    type="button"
                    className={`btn btn-sm ${esActivo ? 'btn-primary' : 'btn-secondary'}`}
                    style={{
                      borderRadius: 20,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: esActivo ? 700 : 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                    }}
                    onClick={() => setEtapaFiltroMuro(col.estado)}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                    <span>{col.titulo}</span>
                    <span style={{ opacity: 0.8, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                      ({col.cantidad})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Selector de Densidad de Tarjetas */}
            <div
              style={{
                display: 'flex',
                gap: 2,
                background: 'var(--bg-surface)',
                padding: 3,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <button
                type="button"
                className={`btn btn-sm ${densidad === 'COMODA' ? 'btn-secondary' : 'btn-ghost'}`}
                style={{
                  fontSize: 11.5,
                  padding: '3px 10px',
                  height: 26,
                  fontWeight: densidad === 'COMODA' ? 700 : 500,
                }}
                onClick={() => setDensidad('COMODA')}
                title="Vista cómoda con espacio y detalles completos"
              >
                ▤ Cómoda
              </button>
              <button
                type="button"
                className={`btn btn-sm ${densidad === 'COMPACTA' ? 'btn-secondary' : 'btn-ghost'}`}
                style={{
                  fontSize: 11.5,
                  padding: '3px 10px',
                  height: 26,
                  fontWeight: densidad === 'COMPACTA' ? 700 : 500,
                }}
                onClick={() => setDensidad('COMPACTA')}
                title="Vista compacta para visualizar más ofertas"
              >
                ▦ Compacta
              </button>
            </div>
          </div>

          {/* Renderizado de Tarjeta Reutilizable */}
          {(() => {
            const renderCard = (tarjeta: TarjetaKanban, colEstado: string, colorPunto: string) => {
              const badgeUrgencia = obtenerBadgeUrgencia(tarjeta.urgencia, tarjeta.diasRestantes);
              const estaSiendoArrastrada = arrastrandoId === tarjeta.id;

              return (
                <div
                  key={tarjeta.id}
                  className={`kanban-card ${densidad === 'COMPACTA' ? 'compacta' : ''} ${estaSiendoArrastrada ? 'moviendo' : ''}`}
                  draggable={esGestorOAdmin}
                  onDragStart={(e) => manejarInicioArrastre(e, tarjeta)}
                  onDragEnd={manejarFinArrastre}
                  style={{
                    position: 'relative',
                    borderLeft: `4px solid ${colorPunto}`,
                    cursor: esGestorOAdmin ? 'grab' : 'pointer',
                  }}
                  onClick={() => setTarjetaParaDetalle(tarjeta)}
                >
                  {/* Fila superior: Código MP, Categoría y Prioridad */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--accent-blue)',
                        background: 'rgba(59, 130, 246, 0.12)',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                      title="Código Mercado Público"
                    >
                      {tarjeta.codigoMercadoPublico}
                    </span>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {tarjeta.categoria && (
                        <span
                          style={{
                            fontSize: 9.5,
                            fontWeight: 600,
                            color: tarjeta.categoria.color,
                            border: `1px solid ${tarjeta.categoria.color}40`,
                            padding: '1px 5px',
                            borderRadius: 3,
                          }}
                        >
                          {tarjeta.categoria.nombre}
                        </span>
                      )}
                      {tarjeta.prioridad === 'ALTA' && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: 'var(--accent-red)',
                            background: 'rgba(239, 68, 68, 0.15)',
                            padding: '1px 4px',
                            borderRadius: 3,
                          }}
                        >
                          ALTA
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Título de la licitación */}
                  <h4
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.35,
                      marginBottom: 4,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {tarjeta.nombre}
                  </h4>

                  {/* Organismo comprador */}
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
                    🏛️ {tarjeta.organismo.nombre}
                  </div>

                  {/* Monto y Estado Económico */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginTop: 6,
                      paddingTop: 6,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                  >
                    <div>
                      <div
                        className="td-mono"
                        style={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: colEstado === 'ADJUDICADA' ? 'var(--accent-green)' : 'var(--text-primary)',
                        }}
                      >
                        {formatearCLP(tarjeta.montoNeto)}
                      </div>
                      <span style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>
                        Neto ({tarjeta.modalidadMonto === 'BRUTO' ? 'base bruto' : 'base neto'})
                      </span>
                    </div>

                    {/* Badge de Urgencia / Días restantes */}
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: badgeUrgencia.fondo,
                        color: badgeUrgencia.color,
                        border: badgeUrgencia.borde,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {badgeUrgencia.texto}
                    </span>
                  </div>

                  {/* Información de Proyecto Asociado (si está ADJUDICADA) */}
                  {tarjeta.proyecto && (
                    <div
                      className="meta-secundaria"
                      style={{
                        marginTop: 8,
                        padding: '6px 8px',
                        background: 'rgba(16, 185, 129, 0.08)',
                        border: '1px solid rgba(16, 185, 129, 0.25)',
                        borderRadius: 4,
                        fontSize: 10.5,
                        color: 'var(--accent-green)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navegar('/proyectos');
                      }}
                      title="Hacer clic para ir al módulo de proyectos"
                    >
                      <span>🚀 Proyecto {tarjeta.proyecto.codigo}</span>
                      <span style={{ fontSize: 9.5, textDecoration: 'underline' }}>Ver proyecto →</span>
                    </div>
                  )}

                  {/* Información de Ganador Rival (si está PERDIDA) */}
                  {colEstado === 'PERDIDA' && tarjeta.ganador && (
                    <div
                      className="meta-secundaria"
                      style={{
                        marginTop: 8,
                        padding: '6px 8px',
                        background: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: 4,
                        fontSize: 10.5,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.4,
                      }}
                    >
                      <div style={{ color: 'var(--accent-red)', fontWeight: 600 }}>
                        Ganó: {tarjeta.ganador.nombre}
                      </div>
                      <div>
                        Oferta ganadora: {formatearCLP(tarjeta.ganador.montoNeto)}
                        {tarjeta.ganador.diferenciaNeto !== 0 && (
                          <span style={{ color: tarjeta.ganador.diferenciaNeto > 0 ? 'var(--accent-red)' : 'var(--accent-green)', fontWeight: 600 }}>
                            {' '}({tarjeta.ganador.diferenciaNeto > 0 ? '+' : ''}
                            {formatearCLP(tarjeta.ganador.diferenciaNeto)})
                          </span>
                        )}
                      </div>
                      {tarjeta.motivoPerdida && (
                        <div style={{ fontSize: 9.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          Causa: {ETIQUETAS_MOTIVO_PERDIDA[tarjeta.motivoPerdida as MotivoPerdida] || tarjeta.motivoPerdida}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pie de tarjeta: Responsable, Eliminar Directo y Menú de Mover Etapa */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 8,
                      paddingTop: 6,
                      borderTop: '1px solid var(--border-subtle)',
                    }}
                  >
                    <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                      👤 {tarjeta.responsable?.nombre || 'Sin asignar'}
                    </span>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {esGestorOAdmin && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            height: 22,
                            color: 'var(--accent-red)',
                            borderColor: 'rgba(239, 68, 68, 0.3)',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTarjetaParaEliminar(tarjeta);
                          }}
                          title="Eliminar esta oferta directamente"
                        >
                          <IconoBasura size={12} />
                        </button>
                      )}

                      {esGestorOAdmin && (
                        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: 10, padding: '2px 6px', height: 22 }}
                            onClick={() => setMenuMoverAbiertoId(menuMoverAbiertoId === tarjeta.id ? null : tarjeta.id)}
                          >
                            Mover ▾
                          </button>

                          {menuMoverAbiertoId === tarjeta.id && (
                            <div
                              style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 26,
                                zIndex: 50,
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border-medium)',
                                borderRadius: 6,
                                padding: 4,
                                boxShadow: 'var(--shadow-elevated)',
                                minWidth: 155,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                              }}
                            >
                              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', padding: '4px 8px' }}>
                                Cambiar a etapa:
                              </div>
                              {columnas.map((colDest) => {
                                if (colDest.estado === colEstado) return null;
                                return (
                                  <button
                                    key={colDest.estado}
                                    type="button"
                                    style={{
                                      textAlign: 'left',
                                      background: 'none',
                                      border: 'none',
                                      padding: '5px 8px',
                                      fontSize: 11,
                                      color: 'var(--text-primary)',
                                      cursor: 'pointer',
                                      borderRadius: 4,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'var(--bg-surface)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'none';
                                    }}
                                    onClick={() => {
                                      setMenuMoverAbiertoId(null);
                                      procesarCambioDeEtapa(tarjeta, colDest.estado as EstadoLicitacion);
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        background: COLORES_ESTADO[colDest.estado],
                                      }}
                                    />
                                    {colDest.titulo}
                                  </button>
                                );
                              })}

                              <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 4, paddingTop: 4 }}>
                                <button
                                  type="button"
                                  style={{
                                    textAlign: 'left',
                                    background: 'none',
                                    border: 'none',
                                    padding: '5px 8px',
                                    fontSize: 11,
                                    color: 'var(--accent-red)',
                                    cursor: 'pointer',
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    width: '100%',
                                  }}
                                  onClick={() => {
                                    setMenuMoverAbiertoId(null);
                                    setTarjetaParaEliminar(tarjeta);
                                  }}
                                >
                                  <IconoBasura size={12} />
                                  Eliminar oferta
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            };

            // CASO A: VISTA ENFOCADA EN UNA SOLA ETAPA (Grid Amplio)
            if (etapaFiltroMuro !== 'TODAS') {
              const colSeleccionada = columnas.find((c) => c.estado === etapaFiltroMuro);
              if (!colSeleccionada) return null;
              const colorPunto = COLORES_ESTADO[colSeleccionada.estado] || 'var(--accent-blue)';

              return (
                <div>
                  <Panel style={{ marginBottom: 18, padding: 18, borderLeft: `5px solid ${colorPunto}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: colorPunto }} />
                          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
                            {colSeleccionada.titulo}
                          </h2>
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: 'var(--text-secondary)',
                              background: 'var(--bg-surface)',
                              border: '1px solid var(--border-subtle)',
                              padding: '2px 9px',
                              borderRadius: 14,
                            }}
                          >
                            {colSeleccionada.cantidad} ofertas
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          Monto total en esta etapa: <strong style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{formatearCLP(colSeleccionada.montoTotalNeto)}</strong>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEtapaFiltroMuro('TODAS')}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <span>← Ver Todas las Columnas</span>
                      </button>
                    </div>
                  </Panel>

                  {colSeleccionada.tarjetas.length === 0 ? (
                    <Panel style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      Sin ofertas registradas en {colSeleccionada.titulo.toLowerCase()}
                    </Panel>
                  ) : (
                    <div className="kanban-focused-grid">
                      {colSeleccionada.tarjetas.map((t) => renderCard(t, colSeleccionada.estado, colorPunto))}
                    </div>
                  )}
                </div>
              );
            }

            // CASO B: TABLERO KANBAN CON LAS 5 COLUMNAS AMPLIAS (330px cada una)
            return (
              <div className="kanban-board">
                {columnas.map((col) => {
                  const colorBorde = BORDES_COLUMNA[col.estado] || 'var(--border-subtle)';
                  const colorPunto = COLORES_ESTADO[col.estado] || 'var(--accent-blue)';
                  const estaHover = columnaHover === col.estado;

                  const CARDS_POR_PAGINA = densidad === 'COMPACTA' ? 8 : 6;
                  const totalTarjetas = col.tarjetas.length;
                  const totalPaginas = Math.max(1, Math.ceil(totalTarjetas / CARDS_POR_PAGINA));
                  const paginaActual = Math.min(totalPaginas, Math.max(1, paginaPorColumna[col.estado] || 1));
                  const inicio = (paginaActual - 1) * CARDS_POR_PAGINA;
                  const tarjetasVisibles = col.tarjetas.slice(inicio, inicio + CARDS_POR_PAGINA);

                  return (
                    <div
                      key={col.estado}
                      className={`kanban-col ${estaHover ? 'arrastrando' : ''}`}
                      style={{
                        borderTop: `4px solid ${colorPunto}`,
                        borderColor: estaHover ? colorPunto : colorBorde,
                        background: estaHover ? 'rgba(59, 130, 246, 0.08)' : undefined,
                        transition: 'all 0.15s ease',
                      }}
                      onDragOver={(e) => manejarArrastrarSobreColumna(e, col.estado)}
                      onDrop={() => manejarSoltarEnColumna(col.estado)}
                    >
                      {/* Cabecera de Columna */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                        <div>
                          <h3
                            style={{
                              fontSize: 13,
                              textTransform: 'uppercase',
                              color: 'var(--text-primary)',
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 7,
                              letterSpacing: '0.03em',
                              marginBottom: 4,
                            }}
                          >
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: colorPunto }} />
                            {col.titulo}
                          </h3>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {formatearCLP(col.montoTotalNeto)}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 11,
                              fontWeight: 700,
                              color: 'var(--text-secondary)',
                              background: 'var(--bg-surface)',
                              border: '1px solid var(--border-subtle)',
                              padding: '2px 7px',
                              borderRadius: 12,
                            }}
                            title={`${col.cantidad} ofertas`}
                          >
                            {col.cantidad}
                          </span>

                          {/* Paginador por Columna (si hay más de 6 ofertas) */}
                          {totalPaginas > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                              <button
                                type="button"
                                disabled={paginaActual <= 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cambiarPaginaColumna(col.estado, -1, totalPaginas);
                                }}
                                style={{
                                  background: 'var(--bg-surface)',
                                  border: '1px solid var(--border-subtle)',
                                  color: paginaActual <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                                  borderRadius: 4,
                                  padding: '2px 5px',
                                  cursor: paginaActual <= 1 ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  lineHeight: 1,
                                  opacity: paginaActual <= 1 ? 0.35 : 1,
                                }}
                                title="Página anterior"
                              >
                                ◀
                              </button>

                              <span
                                style={{
                                  fontSize: 10,
                                  fontFamily: 'var(--font-mono)',
                                  color: 'var(--text-muted)',
                                  minWidth: 28,
                                  textAlign: 'center',
                                }}
                              >
                                {paginaActual}/{totalPaginas}
                              </span>

                              <button
                                type="button"
                                disabled={paginaActual >= totalPaginas}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cambiarPaginaColumna(col.estado, 1, totalPaginas);
                                }}
                                style={{
                                  background: 'var(--bg-surface)',
                                  border: '1px solid var(--border-subtle)',
                                  color: paginaActual >= totalPaginas ? 'var(--text-muted)' : 'var(--text-primary)',
                                  borderRadius: 4,
                                  padding: '2px 5px',
                                  cursor: paginaActual >= totalPaginas ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  lineHeight: 1,
                                  opacity: paginaActual >= totalPaginas ? 0.35 : 1,
                                }}
                                title="Página siguiente"
                              >
                                ▶
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Lista Cómoda de Tarjetas (Sin cajas con scrollbar ni tarjetas cortadas) */}
                      <div className="kanban-cards-container">
                        {tarjetasVisibles.map((tarjeta) => renderCard(tarjeta, col.estado, colorPunto))}

                        {col.tarjetas.length === 0 && (
                          <div
                            style={{
                              color: 'var(--text-muted)',
                              fontSize: 12,
                              textAlign: 'center',
                              padding: '36px 12px',
                              border: '1px dashed var(--border-subtle)',
                              borderRadius: 'var(--radius-sm)',
                            }}
                          >
                            Sin ofertas en {col.titulo.toLowerCase()}
                          </div>
                        )}
                      </div>

                      {/* Pie sutil de columna paginada */}
                      {totalPaginas > 1 && (
                        <div
                          style={{
                            marginTop: 12,
                            paddingTop: 8,
                            borderTop: '1px solid var(--border-subtle)',
                            fontSize: 10.5,
                            color: 'var(--text-muted)',
                            textAlign: 'center',
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          Pág. {paginaActual} de {totalPaginas} ({inicio + 1}–{Math.min(totalTarjetas, inicio + CARDS_POR_PAGINA)} de {totalTarjetas})
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      ) : (
        /* Vista de Lista / Tabla Ejecutiva para auditoría masiva */
        <Panel style={{ padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--bg-surface)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              Listado Consolidado del Pipeline ({todasLasOfertas.length} ofertas totales)
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Mostrando todas las etapas comerciales con filtros aplicados
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Etapa</th>
                  <th>Código MP / Licitación</th>
                  <th>Organismo</th>
                  <th>Categoría</th>
                  <th style={{ textAlign: 'right' }}>Monto Neto</th>
                  <th>Vencimiento</th>
                  <th>Responsable</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {todasLasOfertas.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 36, color: 'var(--text-muted)' }}>
                      No se encontraron ofertas con los filtros seleccionados
                    </td>
                  </tr>
                ) : (
                  todasLasOfertas.map((t) => {
                    const badgeUrgencia = obtenerBadgeUrgencia(t.urgencia, t.diasRestantes);
                    const colorEtapa = COLORES_ESTADO[t.estadoColumna] || 'var(--accent-blue)';
                    return (
                      <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setTarjetaParaDetalle(t)}>
                        <td>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              fontSize: 10.5,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 12,
                              background: `${colorEtapa}18`,
                              color: colorEtapa,
                              border: `1px solid ${colorEtapa}40`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: colorEtapa }} />
                            {t.tituloColumna}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 12.5, marginBottom: 2 }}>
                            {t.nombre}
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              fontFamily: 'var(--font-mono)',
                              color: 'var(--accent-blue)',
                            }}
                          >
                            {t.codigoMercadoPublico}
                          </span>
                        </td>
                        <td style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                          {t.organismo.nombre}
                        </td>
                        <td>
                          {t.categoria ? (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: t.categoria.color,
                                border: `1px solid ${t.categoria.color}40`,
                                padding: '1px 6px',
                                borderRadius: 3,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t.categoria.nombre}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                          {formatearCLP(t.montoNeto)}
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: badgeUrgencia.fondo,
                              color: badgeUrgencia.color,
                              border: badgeUrgencia.borde,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {badgeUrgencia.texto}
                          </span>
                        </td>
                        <td style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {t.responsable?.nombre || '—'}
                        </td>
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: 10, padding: '3px 7px', height: 24 }}
                              onClick={() => setTarjetaParaDetalle(t)}
                              title="Ver ficha completa"
                            >
                              Ficha
                            </button>
                            {esGestorOAdmin && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{
                                  fontSize: 10,
                                  padding: '3px 7px',
                                  height: 24,
                                  color: 'var(--accent-red)',
                                  borderColor: 'rgba(239, 68, 68, 0.3)',
                                }}
                                onClick={() => setTarjetaParaEliminar(t)}
                                title="Eliminar oferta"
                              >
                                <IconoBasura size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* ── 5. Modales Operacionales ──────────────────────────────── */}

      {/* Modal 1: Creación Rápida de Oferta con Asistente del Motor Predictivo IA */}
      {modalCrearAbierto && (
        <ModalNuevaOfertaRapida
          alCerrar={() => setModalCrearAbierto(false)}
          alGuardarExitoso={() => {
            setModalCrearAbierto(false);
            void refetch();
          }}
        />
      )}

      {/* Modal 2: Adjudicación Directa (Puente Comercial ➔ Proyecto) */}
      {tarjetaParaAdjudicar && (
        <ModalAdjudicarLicitacion
          tarjeta={tarjetaParaAdjudicar}
          alCerrar={() => setTarjetaParaAdjudicar(null)}
          alAdjudicarExitoso={() => {
            setTarjetaParaAdjudicar(null);
            avisos.exito('¡Licitación adjudicada con éxito! Se ha generado el Proyecto de operaciones.');
            void refetch();
          }}
        />
      )}

      {/* Modal 3: Registro de Licitación Perdida con Rival Ganador */}
      {tarjetaParaPerder && (
        <ModalRegistrarPerdida
          tarjeta={tarjetaParaPerder}
          alCerrar={() => setTarjetaParaPerder(null)}
          alGuardarExitoso={() => {
            setTarjetaParaPerder(null);
            avisos.exito('Derrota registrada y alimentada al radar de competencia.');
            void refetch();
          }}
        />
      )}

      {/* Modal 4: Ficha Rápida 360° */}
      {tarjetaParaDetalle && (
        <ModalFichaRapida
          tarjeta={tarjetaParaDetalle}
          alCerrar={() => setTarjetaParaDetalle(null)}
          onAdjudicar={() => {
            const t = tarjetaParaDetalle;
            setTarjetaParaDetalle(null);
            setTarjetaParaAdjudicar(t);
          }}
          onPerder={() => {
            const t = tarjetaParaDetalle;
            setTarjetaParaDetalle(null);
            setTarjetaParaPerder(t);
          }}
          onEliminar={() => {
            const t = tarjetaParaDetalle;
            setTarjetaParaDetalle(null);
            setTarjetaParaEliminar(t);
          }}
        />
      )}

      {/* Modal 5: Confirmación de Eliminación Directa */}
      {tarjetaParaEliminar && (
        <ModalEliminarLicitacion
          tarjeta={tarjetaParaEliminar}
          alCerrar={() => setTarjetaParaEliminar(null)}
          alEliminarExitoso={() => {
            setTarjetaParaEliminar(null);
            void refetch();
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// COMPONENTES AUXILIARES DEL ASISTENTE PREDICTIVO
// ─────────────────────────────────────────────────────────────────

function CeldaResumen({ etiqueta, valor, color, nota }: { etiqueta: string; valor: string; color?: string; nota?: string }) {
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{etiqueta}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, marginTop: 4, color: color ?? 'var(--text-primary)' }}>
        {valor}
      </div>
      {nota && <div style={{ fontSize: 9.5, color: 'var(--text-muted)', marginTop: 2 }}>{nota}</div>}
    </div>
  );
}

function nivelColor(nivel: string): string {
  if (nivel === 'ALTA') return 'var(--accent-green)';
  if (nivel === 'MEDIA') return 'var(--accent-yellow)';
  return 'var(--accent-red)';
}

function nivelFondo(nivel: string): string {
  if (nivel === 'ALTA') return 'var(--accent-green-soft)';
  if (nivel === 'MEDIA') return 'var(--accent-yellow-soft)';
  return 'var(--accent-red-soft)';
}

// ─────────────────────────────────────────────────────────────────
// MODAL 1: NUEVA OFERTA RÁPIDA (CON MOTOR PREDICTIVO IA INTEGRADO)
// ─────────────────────────────────────────────────────────────────

interface ModalNuevaOfertaProps {
  alCerrar: () => void;
  alGuardarExitoso: () => void;
}

function ModalNuevaOfertaRapida({ alCerrar, alGuardarExitoso }: ModalNuevaOfertaProps) {
  const avisos = useAvisos();
  const mutacionCrear = useCrearLicitacionRapida();
  const categorias = useCategoriasComercialesConfig();
  const organismos = useOrganismos();
  const usuarios = useUsuariosEmpresa();

  const [codigoMercadoPublico, setCodigoMercadoPublico] = useState('');
  const [nombre, setNombre] = useState('');
  const [nombreOrganismo, setNombreOrganismo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [tipoProceso, setTipoProceso] = useState<TipoProceso>('LICITACION');
  const [prioridad, setPrioridad] = useState<Prioridad>('MEDIA');
  const [estadoInicial, setEstadoInicial] = useState<EstadoLicitacion>('OPORTUNIDAD');
  const [modalidadMonto, setModalidadMonto] = useState<ModalidadMonto>('NETO');

  const [montoOfertadoNeto, setMontoOfertadoNeto] = useState<number>(0);
  const [montoOfertadoBruto, setMontoOfertadoBruto] = useState<number>(0);
  const [presupuestoOficialNeto, setPresupuestoOficialNeto] = useState<number>(0);
  const [presupuestoOficialBruto, setPresupuestoOficialBruto] = useState<number>(0);

  const [fechaCierrePostulacion, setFechaCierrePostulacion] = useState('');
  const [plazoEntregaOfertadoDias, setPlazoEntregaOfertadoDias] = useState<number>(30);
  const [duracionMeses, setDuracionMeses] = useState<number>(12);
  const [responsableId, setResponsableId] = useState('');

  const [precioElegido, setPrecioElegido] = useState<number | null>(null);

  // Sincronización bidireccional del presupuesto oficial (Neto ↔ Bruto con IVA 19%)
  const handleCambioPresupuestoNeto = (valor: number) => {
    setPresupuestoOficialNeto(valor);
    if (valor > 0) {
      const cal = calcularDesdeNeto(valor, 19);
      setPresupuestoOficialBruto(cal.montoTotal);
    } else {
      setPresupuestoOficialBruto(0);
    }
  };

  const handleCambioPresupuestoBruto = (valor: number) => {
    setPresupuestoOficialBruto(valor);
    if (valor > 0) {
      const cal = calcularDesdeTotal(valor, 19);
      setPresupuestoOficialNeto(cal.montoNeto);
    } else {
      setPresupuestoOficialNeto(0);
    }
  };

  // Buscar coincidencia de organismo en tiempo real para ponderar su historial y sector
  const organismoMatch = useMemo(() => {
    if (!nombreOrganismo.trim()) return null;
    return (
      organismos.data?.find(
        (o) => o.nombre.toLowerCase().trim() === nombreOrganismo.toLowerCase().trim(),
      ) ?? null
    );
  }, [nombreOrganismo, organismos.data]);

  // 🧠 INTEGRACIÓN MOTOR PREDICTIVO INTELIGENTE
  const entradaPrediccion = useMemo<EntradaPrediccion | null>(() => {
    if (!categoriaId || !presupuestoOficialNeto || presupuestoOficialNeto <= 0) return null;
    return {
      categoriaId,
      presupuestoOficialNeto,
      presupuestoOficialBruto: presupuestoOficialBruto > 0 ? presupuestoOficialBruto : undefined,
      modalidadMonto,
      organismoId: organismoMatch?.id || null,
      tipoOrganismo: organismoMatch?.tipo || null,
      tipoProceso,
      plazoMaximoBasesDias: plazoEntregaOfertadoDias || null,
    };
  }, [categoriaId, presupuestoOficialNeto, presupuestoOficialBruto, modalidadMonto, organismoMatch, tipoProceso, plazoEntregaOfertadoDias]);

  const consultaPrediccion = usePrediccion(entradaPrediccion);
  const prediccion = consultaPrediccion.data;
  const categoriaSeleccionada = categorias.data?.find((c) => c.id === categoriaId);

  const aplicarRecomendacionIA = (precioManual?: number) => {
    if (!prediccion) return;
    const precioNetoFinal = precioManual ?? precioElegido ?? prediccion.precio.precioSugerido;
    if (precioNetoFinal && precioNetoFinal > 0) {
      const netoRedondeado = Math.round(precioNetoFinal);
      setMontoOfertadoNeto(netoRedondeado);
      const cal = calcularDesdeNeto(netoRedondeado, 19);
      setMontoOfertadoBruto(cal.montoTotal);
      avisos.exito(`Recomendación IA aplicada: ${formatearCLP(netoRedondeado)} Neto (${formatearCLP(cal.montoTotal)} con IVA)`);
    }
    if (prediccion.plazo.plazoSugeridoDias) {
      setPlazoEntregaOfertadoDias(Math.round(prediccion.plazo.plazoSugeridoDias));
    }
  };

  // Cálculo tributario 19% IVA en tiempo real
  const handleCambioNeto = (valor: number) => {
    setMontoOfertadoNeto(valor);
    const cal = calcularDesdeNeto(valor, 19);
    setMontoOfertadoBruto(cal.montoTotal);
  };

  const handleCambioBruto = (valor: number) => {
    setMontoOfertadoBruto(valor);
    const cal = calcularDesdeTotal(valor, 19);
    setMontoOfertadoNeto(cal.montoNeto);
  };

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigoMercadoPublico.trim()) {
      avisos.error('Indica el código de Mercado Público (ej: 2405-12-LP26)');
      return;
    }
    if (!nombre.trim()) {
      avisos.error('El nombre de la licitación es obligatorio');
      return;
    }
    if (!nombreOrganismo.trim()) {
      avisos.error('Indica la institución o comprador');
      return;
    }
    if (!categoriaId) {
      avisos.error('Selecciona una línea comercial o categoría');
      return;
    }

    const payload: CrearLicitacionInput = {
      codigoMercadoPublico: codigoMercadoPublico.trim(),
      nombre: nombre.trim(),
      nombreOrganismo: nombreOrganismo.trim(),
      categoriaId,
      tipoProceso,
      estado: estadoInicial,
      prioridad,
      modalidadMonto,
      presupuestoOficialNeto: presupuestoOficialNeto || montoOfertadoNeto,
      presupuestoOficialBruto: presupuestoOficialBruto || montoOfertadoBruto,
      montoOfertadoNeto: montoOfertadoNeto || 0,
      montoOfertadoBruto: montoOfertadoBruto || 0,
      fechaCierrePostulacion: fechaCierrePostulacion ? new Date(fechaCierrePostulacion).toISOString() : undefined,
      plazoEntregaOfertadoDias: plazoEntregaOfertadoDias || 0,
      plazoMaximoBasesDias: plazoEntregaOfertadoDias || 0,
      duracionMeses: duracionMeses || 1,
      responsableId: responsableId || undefined,
    };

    mutacionCrear.mutate(payload, {
      onSuccess: () => {
        avisos.exito('¡Oferta creada exitosamente en el Pipeline!');
        alGuardarExitoso();
      },
      onError: (err: any) => {
        avisos.error(err?.message || 'Error al crear la licitación');
      },
    });
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 900, maxHeight: '92vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800 }}>⚡ Nueva Oferta Comercial (30 seg)</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Registra rápidamente una licitación. El sistema creará el comprador automáticamente si no existe.
            </p>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
            <IconoCerrar size={16} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-grid">
              <div className="form-group">
                <label>Código Mercado Público *</label>
                <input
                  type="text"
                  placeholder="Ej: 1234-56-LP26"
                  value={codigoMercadoPublico}
                  onChange={(e) => setCodigoMercadoPublico(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Tipo de Proceso</label>
                <select value={tipoProceso} onChange={(e) => setTipoProceso(e.target.value as TipoProceso)}>
                  {TIPOS_PROCESO.map((tp) => (
                    <option key={tp} value={tp}>
                      {ETIQUETAS_TIPO_PROCESO[tp]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group ancho-total">
                <label>Nombre de la Licitación / Servicio *</label>
                <input
                  type="text"
                  placeholder="Ej: Servicio de Soporte Técnico y Mantenimiento de Servidores"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Organismo Comprador *</label>
                <input
                  type="text"
                  list="lista-organismos-sugeridos"
                  placeholder="Ej: Municipalidad de Las Condes"
                  value={nombreOrganismo}
                  onChange={(e) => setNombreOrganismo(e.target.value)}
                  required
                />
                <datalist id="lista-organismos-sugeridos">
                  {organismos.data?.map((org) => (
                    <option key={org.id} value={org.nombre} />
                  ))}
                </datalist>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Escribe uno nuevo o selecciona uno del catálogo
                </span>
              </div>

              <div className="form-group">
                <label>Línea Comercial / Categoría *</label>
                <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} required>
                  <option value="">Selecciona línea de negocio…</option>
                  {categorias.data?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Prioridad de Oportunidad</label>
                <select value={prioridad} onChange={(e) => setPrioridad(e.target.value as Prioridad)}>
                  {PRIORIDADES.map((pri) => (
                    <option key={pri} value={pri}>
                      {ETIQUETAS_PRIORIDAD[pri]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Etapa Inicial</label>
                <select value={estadoInicial} onChange={(e) => setEstadoInicial(e.target.value as EstadoLicitacion)}>
                  <option value="OPORTUNIDAD">Oportunidad</option>
                  <option value="EN_PREPARACION">En Preparación</option>
                  <option value="OFERTA_ENVIADA">Oferta Enviada</option>
                </select>
              </div>

              <div className="form-group">
                <label>Modalidad de Monto</label>
                <select value={modalidadMonto} onChange={(e) => setModalidadMonto(e.target.value as ModalidadMonto)}>
                  <option value="NETO">Neto (sin IVA)</option>
                  <option value="BRUTO">Bruto (con IVA)</option>
                </select>
              </div>
            </div>

            {/* Presupuesto Oficial & Tarjeta Asistente Motor Predictivo IA */}
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
                padding: 14,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
                  💰 Presupuesto y Motor Predictivo IA
                </span>
                {consultaPrediccion.isFetching && (
                  <span style={{ fontSize: 10.5, color: 'var(--accent-purple)' }}>Consultando IA…</span>
                )}
              </div>

              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                <div className="form-group">
                  <label>Presupuesto Oficial Publicado (Neto CLP)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Ej: 50000000"
                    value={presupuestoOficialNeto || ''}
                    onChange={(e) => handleCambioPresupuestoNeto(Number(e.target.value))}
                  />
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    Sin IVA: {formatearCLP(presupuestoOficialNeto)}
                  </span>
                </div>

                <div className="form-group">
                  <label>Presupuesto Oficial Publicado (Bruto con IVA)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Ej: 59500000"
                    value={presupuestoOficialBruto || ''}
                    onChange={(e) => handleCambioPresupuestoBruto(Number(e.target.value))}
                  />
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    Con 19% IVA: {formatearCLP(presupuestoOficialBruto)}
                  </span>
                </div>

                <div className="form-group">
                  <label>Fecha y Hora Cierre de Postulación</label>
                  <input
                    type="datetime-local"
                    value={fechaCierrePostulacion}
                    onChange={(e) => setFechaCierrePostulacion(e.target.value)}
                  />
                </div>
              </div>

              {/* Sugerencia inteligente del motor en vivo (Exacta a Imagen 2) */}
              {consultaPrediccion.isLoading && entradaPrediccion && (
                <div style={{ marginTop: 12 }}>
                  <Cargando texto="Consultando histórico de compras públicas y calculando precio y plazo óptimos…" />
                </div>
              )}

              {prediccion && (
                <div
                  style={{
                    marginTop: 14,
                    background: 'var(--accent-blue-soft)',
                    border: '1px solid var(--accent-blue)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '16px 18px',
                  }}
                >
                  {/* Cabecera del asistente */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 14,
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <IconoCerebro size={22} style={{ color: 'var(--accent-blue)' }} />
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                          Asistente predictivo de precio y plazo
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          modelo {prediccion.modeloVersion}
                          {organismoMatch && ` · Ponderando historial de ${organismoMatch.nombre}`}
                        </div>
                      </div>
                    </div>

                    <span
                      style={{
                        background: nivelFondo(prediccion.precio.evidencia.nivel),
                        color: nivelColor(prediccion.precio.evidencia.nivel),
                        border: `1px solid ${nivelColor(prediccion.precio.evidencia.nivel)}40`,
                        padding: '3px 8px',
                        borderRadius: 12,
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      CONFIANZA {prediccion.precio.evidencia.nivel} · n efectivo{' '}
                      {prediccion.precio.evidencia.nEfectivo.toFixed(2)}
                    </span>
                  </div>

                  {prediccion.precio.precioSugerido === 0 ? (
                    <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      Todavía no hay historial suficiente en esta categoría.
                    </div>
                  ) : (
                    <>
                      {/* Las 4 Celdas de Métricas Clave */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                          gap: 10,
                          marginBottom: 16,
                        }}
                      >
                        <CeldaResumen
                          etiqueta="Precio sugerido"
                          valor={`${formatearCLP(prediccion.precio.precioSugeridoNeto ?? prediccion.precio.precioSugerido)} Neto`}
                          color="var(--accent-blue)"
                          nota={`Con IVA: ${formatearCLP(prediccion.precio.precioSugeridoBruto ?? Math.round(prediccion.precio.precioSugerido * 1.19))} | Rango Neto ${formatearCLP(prediccion.precio.rangoMinNeto ?? prediccion.precio.rangoMin)} – ${formatearCLP(prediccion.precio.rangoMaxNeto ?? prediccion.precio.rangoMax)}`}
                        />
                        <CeldaResumen
                          etiqueta="Precio del mercado"
                          valor={formatearPorcentajeSimple(prediccion.precio.ratioMercado * 100, 1)}
                          nota="del presupuesto oficial"
                        />
                        <CeldaResumen
                          etiqueta="Piso por margen"
                          valor={
                            (prediccion.precio.pisoPorMargenNeto ?? prediccion.precio.pisoPorMargen) !== null
                              ? `${formatearCLP(prediccion.precio.pisoPorMargenNeto ?? prediccion.precio.pisoPorMargen!)} Neto`
                              : '—'
                          }
                          color="var(--accent-red)"
                          nota={
                            (prediccion.precio.pisoPorMargenNeto ?? prediccion.precio.pisoPorMargen) === null
                              ? 'sin datos de costo'
                              : categoriaSeleccionada
                                ? `margen obj: ${categoriaSeleccionada.margenObjetivoPct}% | Con IVA: ${formatearCLP(prediccion.precio.pisoPorMargenBruto ?? Math.round((prediccion.precio.pisoPorMargen ?? 0) * 1.19))}`
                                : `Con IVA: ${formatearCLP(prediccion.precio.pisoPorMargenBruto ?? Math.round((prediccion.precio.pisoPorMargen ?? 0) * 1.19))}`
                          }
                        />
                        <CeldaResumen
                          etiqueta="Plazo sugerido"
                          valor={prediccion.plazo.plazoSugeridoDias === null ? '—' : `${prediccion.plazo.plazoSugeridoDias} días`}
                          nota={
                            prediccion.plazo.limitadoPorBases
                              ? 'ajustado al tope de las bases'
                              : plazoEntregaOfertadoDias > 0
                                ? `tope bases: ${plazoEntregaOfertadoDias}d`
                                : 'sin tope indicado'
                          }
                        />
                      </div>

                      {/* Curva de Probabilidad Interactiva */}
                      {prediccion.precio.curvaProbabilidad.length > 0 && (
                        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 14 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                            <span>PRECIO Y PROBABILIDAD DE GANAR</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Toca un punto para evaluarlo</span>
                          </div>
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 8 }}>
                            Estimada sobre los precios con que se ganaron procesos comparables: en qué proporción de ellos tu precio habría bastado.
                          </div>
                          <CurvaProbabilidad
                            curva={prediccion.precio.curvaProbabilidad}
                            pisoPorMargen={prediccion.precio.pisoPorMargen}
                            precioSugerido={prediccion.precio.precioSugerido}
                            precioElegido={precioElegido}
                            alElegir={setPrecioElegido}
                            modalidadMonto={modalidadMonto}
                          />
                        </div>
                      )}

                      {/* Detalle de Evidencia & Advertencias */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            lineHeight: 1.5,
                            padding: '8px 12px',
                            background: 'var(--bg-elevated)',
                            borderRadius: 'var(--radius-sm)',
                          }}
                        >
                          {prediccion.precio.evidencia.detalle}
                        </div>

                        {prediccion.precio.advertencias.map((adv, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              padding: '7px 10px',
                              background: 'var(--accent-yellow-soft)',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 11,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            <IconoAlerta size={14} style={{ color: 'var(--accent-yellow)', flexShrink: 0 }} />
                            <span>{adv}</span>
                          </div>
                        ))}
                      </div>

                      {/* Botón de Aplicación Rápida */}
                      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        {(() => {
                          const precioNetoElegido = precioElegido ?? prediccion.precio.precioSugeridoNeto ?? prediccion.precio.precioSugerido;
                          const precioBrutoElegido = calcularDesdeNeto(precioNetoElegido, 19).montoTotal;
                          return (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => aplicarRecomendacionIA(precioNetoElegido)}
                              style={{ gap: 6 }}
                            >
                              <IconoRayo size={14} />
                              <span>
                                Usar {formatearCLP(precioNetoElegido)} Neto ({formatearCLP(precioBrutoElegido)} con IVA)
                              </span>
                            </button>
                          );
                        })()}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          Al presionar, este valor neto y su IVA 19% se transfieren directamente a tu oferta económica abajo.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {!prediccion && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    background: 'var(--bg-elevated)',
                    border: '1px dashed var(--border-subtle)',
                    borderRadius: 6,
                    fontSize: 11.5,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <IconoCerebro size={16} style={{ color: 'var(--accent-blue)', flexShrink: 0 }} />
                  <span>
                    Indica la <strong>categoría comercial</strong> y el <strong>presupuesto oficial</strong> arriba para que el motor predictivo estime en tiempo real el precio y plazo óptimos según el historial de compras públicas.
                  </span>
                </div>
              )}
            </div>

            {/* Monto Ofertado & IVA */}
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
                padding: 14,
              }}
            >
              <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 10, color: 'var(--text-secondary)' }}>
                🏷️ Nuestra Oferta Económica (con desglose IVA 19%)
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Monto Ofertado Neto (CLP)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={montoOfertadoNeto || ''}
                    onChange={(e) => handleCambioNeto(Number(e.target.value))}
                  />
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    Sin IVA: {formatearCLP(montoOfertadoNeto)}
                  </span>
                </div>

                <div className="form-group">
                  <label>Monto Ofertado Bruto (con IVA)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={montoOfertadoBruto || ''}
                    onChange={(e) => handleCambioBruto(Number(e.target.value))}
                  />
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    Total con IVA 19%: {formatearCLP(montoOfertadoBruto)}
                  </span>
                </div>
              </div>
            </div>

            {/* Plazos y Responsable */}
            <div className="form-grid">
              <div className="form-group">
                <label>Plazo Entrega Ofertado (Días)</label>
                <input
                  type="number"
                  min="1"
                  value={plazoEntregaOfertadoDias}
                  onChange={(e) => setPlazoEntregaOfertadoDias(Number(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Duración de Contrato (Meses)</label>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={duracionMeses}
                  onChange={(e) => setDuracionMeses(Number(e.target.value))}
                />
              </div>

              <div className="form-group ancho-total">
                <label>Responsable Comercial</label>
                <select value={responsableId} onChange={(e) => setResponsableId(e.target.value)}>
                  <option value="">Sin asignar (o selecciona del equipo)</option>
                  {usuarios.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutacionCrear.isPending}>
              {mutacionCrear.isPending ? 'Guardando oferta…' : '⚡ Guardar Oferta (30s)'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODAL 2: ADJUDICAR LICITACIÓN (PUENTE COMERCIAL ➔ PROYECTO)
// ─────────────────────────────────────────────────────────────────

interface ModalAdjudicarProps {
  tarjeta: TarjetaKanban;
  alCerrar: () => void;
  alAdjudicarExitoso: () => void;
}

function ModalAdjudicarLicitacion({ tarjeta, alCerrar, alAdjudicarExitoso }: ModalAdjudicarProps) {
  const mutacionAdjudicar = useAdjudicarLicitacionDirecta();
  const avisos = useAvisos();

  const [montoNeto, setMontoNeto] = useState<number>(tarjeta.montoNeto || 0);
  const [montoBruto, setMontoBruto] = useState<number>(tarjeta.montoBruto || calcularDesdeNeto(tarjeta.montoNeto, 19).montoTotal);
  const [modalidad, setModalidad] = useState<ModalidadMonto>((tarjeta.modalidadMonto as ModalidadMonto) || 'NETO');

  const handleNeto = (val: number) => {
    setMontoNeto(val);
    setMontoBruto(calcularDesdeNeto(val, 19).montoTotal);
  };

  const handleBruto = (val: number) => {
    setMontoBruto(val);
    setMontoNeto(calcularDesdeTotal(val, 19).montoNeto);
  };

  const confirmar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!montoNeto || montoNeto <= 0) {
      avisos.error('El monto adjudicado neto debe ser mayor a $0');
      return;
    }

    mutacionAdjudicar.mutate(
      {
        id: tarjeta.id,
        montoAdjudicadoNeto: montoNeto,
        montoAdjudicadoBruto: montoBruto,
        modalidadMonto: modalidad,
      },
      {
        onSuccess: () => {
          alAdjudicarExitoso();
        },
        onError: (err: any) => {
          avisos.error(err?.message || 'Error al adjudicar la licitación');
        },
      },
    );
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-green)' }}>
              🎉 Confirmar Adjudicación de Contrato
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {tarjeta.codigoMercadoPublico} — {tarjeta.nombre}
            </p>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
            <IconoCerrar size={16} />
          </button>
        </div>

        <form onSubmit={confirmar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 8,
                padding: 14,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
              }}
            >
              <strong>🚀 Puente Comercial ➔ Operaciones:</strong>
              <div style={{ marginTop: 4 }}>
                Al confirmar la adjudicación, el sistema ejecutará una transacción automática que:
              </div>
              <ul style={{ marginTop: 6, paddingLeft: 18, listStyleType: 'disc' }}>
                <li>Fijará el monto contractual definitivo de la licitación.</li>
                <li>
                  <strong>Creará automáticamente el Proyecto</strong> en el módulo de Proyectos.
                </li>
                <li>Generará la estructura de presupuesto de costos y cronograma de facturación inicial.</li>
              </ul>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Monto Adjudicado Neto (CLP) *</label>
                <input
                  type="number"
                  min="1"
                  value={montoNeto || ''}
                  onChange={(e) => handleNeto(Number(e.target.value))}
                  required
                />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatearCLP(montoNeto)}</span>
              </div>

              <div className="form-group">
                <label>Monto Adjudicado Bruto (con 19% IVA)</label>
                <input
                  type="number"
                  min="1"
                  value={montoBruto || ''}
                  onChange={(e) => handleBruto(Number(e.target.value))}
                  required
                />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatearCLP(montoBruto)}</span>
              </div>

              <div className="form-group ancho-total">
                <label>Modalidad de Facturación</label>
                <select value={modalidad} onChange={(e) => setModalidad(e.target.value as ModalidadMonto)}>
                  <option value="NETO">Neto (Facturación + IVA 19%)</option>
                  <option value="BRUTO">Bruto (Monto IVA incluido)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={mutacionAdjudicar.isPending}>
              {mutacionAdjudicar.isPending ? 'Creando proyecto…' : '🎉 Confirmar y Crear Proyecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODAL 3: REGISTRO DE DERROTA (ALIMENTAR RADAR DE COMPETENCIA)
// ─────────────────────────────────────────────────────────────────

interface ModalPerdidaProps {
  tarjeta: TarjetaKanban;
  alCerrar: () => void;
  alGuardarExitoso: () => void;
}

function ModalRegistrarPerdida({ tarjeta, alCerrar, alGuardarExitoso }: ModalPerdidaProps) {
  const mutacionCambiarEstado = useCambiarEstadoLicitacion();
  const avisos = useAvisos();
  const competidores = useCompetidoresCatalogo();

  const [motivoPerdida, setMotivoPerdida] = useState<MotivoPerdida>('PRECIO');
  const [nombreCompetidor, setNombreCompetidor] = useState('');
  const [montoGanadorNeto, setMontoGanadorNeto] = useState<number>(0);
  const [plazoGanadorDias, setPlazoGanadorDias] = useState<number>(0);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombreCompetidor.trim()) {
      avisos.error('Indica el nombre de la empresa rival que ganó la licitación');
      return;
    }
    if (!montoGanadorNeto || montoGanadorNeto <= 0) {
      avisos.error('Indica el monto neto de la oferta ganadora');
      return;
    }

    mutacionCambiarEstado.mutate(
      {
        id: tarjeta.id,
        datos: {
          estado: 'PERDIDA',
          motivoPerdida,
          ganador: {
            nombreCompetidor: nombreCompetidor.trim(),
            montoNeto: montoGanadorNeto,
            plazoDias: plazoGanadorDias || undefined,
          },
        },
      },
      {
        onSuccess: () => {
          alGuardarExitoso();
        },
        onError: (err: any) => {
          avisos.error(err?.message || 'Error al registrar la derrota');
        },
      },
    );
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-red)' }}>
              📉 Registrar Licitación Perdida
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {tarjeta.codigoMercadoPublico} — {tarjeta.nombre}
            </p>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
            <IconoCerrar size={16} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: 8,
                padding: 12,
                fontSize: 12,
                lineHeight: 1.4,
                color: 'var(--text-secondary)',
              }}
            >
              💡 <strong>Inteligencia de Mercado:</strong> Registrar quién ganó y con qué monto alimenta de inmediato
              el <strong>Radar de Competidores</strong> y el <strong>Banco de Precios Ganadores</strong>, permitiendo
              ajustar tus márgenes para ganar la próxima licitación.
            </div>

            <div className="form-grid">
              <div className="form-group ancho-total">
                <label>Motivo Principal de Pérdida *</label>
                <select value={motivoPerdida} onChange={(e) => setMotivoPerdida(e.target.value as MotivoPerdida)}>
                  {MOTIVOS_PERDIDA.map((m) => (
                    <option key={m} value={m}>
                      {ETIQUETAS_MOTIVO_PERDIDA[m]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group ancho-total">
                <label>Competidor Ganador (Empresa) *</label>
                <input
                  type="text"
                  list="lista-rivales-conocidos"
                  placeholder="Ej: TechCorp Chile SpA"
                  value={nombreCompetidor}
                  onChange={(e) => setNombreCompetidor(e.target.value)}
                  required
                />
                <datalist id="lista-rivales-conocidos">
                  {competidores.data?.map((comp) => (
                    <option key={comp.id} value={comp.nombre} />
                  ))}
                </datalist>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Escribe el nombre del adjudicatario según el acta pública
                </span>
              </div>

              <div className="form-group">
                <label>Monto Ganador Neto (CLP) *</label>
                <input
                  type="number"
                  min="1"
                  placeholder="0"
                  value={montoGanadorNeto || ''}
                  onChange={(e) => setMontoGanadorNeto(Number(e.target.value))}
                  required
                />
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Nuestra oferta era: {formatearCLP(tarjeta.montoNeto)}
                </span>
              </div>

              <div className="form-group">
                <label>Plazo Ofertado por Ganador (Días)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Opcional"
                  value={plazoGanadorDias || ''}
                  onChange={(e) => setPlazoGanadorDias(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-danger" disabled={mutacionCambiarEstado.isPending}>
              {mutacionCambiarEstado.isPending ? 'Guardando...' : 'Guardar Derrota e Inteligencia'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODAL 4: FICHA RÁPIDA DE LICITACIÓN (INSPECTOR 360°)
// ─────────────────────────────────────────────────────────────────

interface ModalFichaProps {
  tarjeta: TarjetaKanban;
  alCerrar: () => void;
  onAdjudicar: () => void;
  onPerder: () => void;
  onEliminar: () => void;
}

function ModalFichaRapida({ tarjeta, alCerrar, onAdjudicar, onPerder, onEliminar }: ModalFichaProps) {
  const avisos = useAvisos();
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const esGestorOAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER' || usuario?.rol === 'GESTOR';

  const copiarCodigo = () => {
    navigator.clipboard.writeText(tarjeta.codigoMercadoPublico);
    avisos.exito('Código copiado al portapapeles');
  };

  const abrirEnMercadoPublico = () => {
    window.open(
      `https://www.mercadopublico.cl/FichaLicitacion/Index?codigoLicitacion=${encodeURIComponent(tarjeta.codigoMercadoPublico)}`,
      '_blank',
    );
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--accent-blue)',
                  background: 'rgba(59, 130, 246, 0.12)',
                  padding: '2px 8px',
                  borderRadius: 4,
                }}
              >
                {tarjeta.codigoMercadoPublico}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={copiarCodigo}
                style={{ padding: '2px 6px', height: 22 }}
                title="Copiar código"
              >
                <IconoCopiar size={12} />
              </button>
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 800 }}>{tarjeta.nombre}</h2>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
            <IconoCerrar size={16} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Fila de Tags */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: COLORES_ESTADO[tarjeta.urgencia] || 'var(--text-primary)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                padding: '3px 8px',
                borderRadius: 4,
              }}
            >
              Etapa: {tarjeta.urgencia}
            </span>

            {tarjeta.categoria && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: tarjeta.categoria.color,
                  border: `1px solid ${tarjeta.categoria.color}40`,
                  padding: '3px 8px',
                  borderRadius: 4,
                }}
              >
                📁 {tarjeta.categoria.nombre}
              </span>
            )}

            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                padding: '3px 8px',
                borderRadius: 4,
              }}
            >
              🏛️ {ETIQUETAS_TIPO_ORGANISMO[tarjeta.organismo.tipo as TipoOrganismo] || tarjeta.organismo.tipo}
            </span>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={abrirEnMercadoPublico}
              style={{ marginLeft: 'auto', fontSize: 11 }}
            >
              🌐 Ver en Mercado Público ↗
            </button>
          </div>

          {/* Cuadrícula de Datos Clave */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Organismo Comprador</span>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>{tarjeta.organismo.nombre}</div>
            </div>

            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Responsable Comercial</span>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>{tarjeta.responsable?.nombre || 'No asignado'}</div>
            </div>

            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monto Ofertado Neto</span>
              <div className="td-mono" style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>
                {formatearCLP(tarjeta.montoNeto)}
              </div>
            </div>

            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monto con IVA 19% (Bruto)</span>
              <div className="td-mono" style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent-blue)' }}>
                {formatearCLP(tarjeta.montoBruto)}
              </div>
            </div>

            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Cierre de Postulación</span>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                {formatearFechaHora(tarjeta.fechaCierrePostulacion)}
              </div>
            </div>

            <div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Días Restantes</span>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                {tarjeta.diasRestantes !== null ? `${tarjeta.diasRestantes} días corridos` : '—'}
              </div>
            </div>
          </div>

          {/* Proyecto Vinculado */}
          {tarjeta.proyecto && (
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 8,
                padding: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <span style={{ fontSize: 11, color: 'var(--accent-green)', fontWeight: 700 }}>
                  PROYECTO OPERACIONAL VINCULADO
                </span>
                <div style={{ fontSize: 14, fontWeight: 800 }}>Código: {tarjeta.proyecto.codigo}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Estado: {tarjeta.proyecto.estado}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  alCerrar();
                  navegar('/proyectos');
                }}
              >
                Ir a Proyectos →
              </button>
            </div>
          )}

          {/* Datos de Rival si está Perdida */}
          {tarjeta.ganador && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 8,
                padding: 14,
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--accent-red)', fontWeight: 700 }}>
                INFORMACIÓN DE ADJUDICACIÓN A TERCEROS
              </span>
              <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 4 }}>
                Empresa Ganadora: {tarjeta.ganador.nombre}
              </div>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                Monto Adjudicado: {formatearCLP(tarjeta.ganador.montoNeto)} (Diferencia:{' '}
                <span style={{ color: 'var(--accent-red)', fontWeight: 600 }}>
                  {tarjeta.ganador.diferenciaNeto > 0 ? '+' : ''}
                  {formatearCLP(tarjeta.ganador.diferenciaNeto)}
                </span>
                )
              </div>
              {tarjeta.motivoPerdida && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Motivo: {ETIQUETAS_MOTIVO_PERDIDA[tarjeta.motivoPerdida as MotivoPerdida] || tarjeta.motivoPerdida}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {esGestorOAdmin && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ color: 'var(--accent-red)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                onClick={onEliminar}
              >
                <IconoBasura size={14} />
                <span>Eliminar licitación</span>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            {esGestorOAdmin && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onPerder}>
                  📉 Marcar Perdida
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={onAdjudicar}>
                  🎉 Marcar Adjudicada
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MODAL 5: CONFIRMACIÓN DE ELIMINACIÓN DIRECTA
// ─────────────────────────────────────────────────────────────────

interface ModalEliminarProps {
  tarjeta: TarjetaKanban;
  alCerrar: () => void;
  alEliminarExitoso: () => void;
}

function ModalEliminarLicitacion({ tarjeta, alCerrar, alEliminarExitoso }: ModalEliminarProps) {
  const avisos = useAvisos();
  const mutacionEliminar = useEliminarLicitacion();

  const ejecutarEliminacion = () => {
    mutacionEliminar.mutate(tarjeta.id, {
      onSuccess: () => {
        avisos.exito(`Licitación "${tarjeta.codigoMercadoPublico}" eliminada del Pipeline.`);
        alEliminarExitoso();
      },
      onError: (err: any) => {
        const msg = err?.mensaje || err?.message || 'No se pudo eliminar la licitación.';
        avisos.error(msg);
      },
    });
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--accent-red)' }}>
              <IconoAlerta size={20} />
            </span>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--accent-red)' }}>
              Eliminar Oferta del Pipeline
            </h2>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={alCerrar}>
            <IconoCerrar size={16} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-primary)' }}>
            ¿Estás seguro de que deseas eliminar definitivamente la licitación{' '}
            <strong>{tarjeta.codigoMercadoPublico}</strong> — &ldquo;{tarjeta.nombre}&rdquo;?
          </p>

          {tarjeta.proyecto && (
            <div
              style={{
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 6,
                padding: 10,
                fontSize: 12,
                color: 'var(--accent-blue)',
              }}
            >
              ℹ️ <strong>Información:</strong> Esta licitación tiene un proyecto operacional vinculado (
              {tarjeta.proyecto.codigo}). Al eliminarla, el proyecto quedará desvinculado automáticamente sin causar errores ni pérdida de datos operacionales.
            </div>
          )}

          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              padding: 10,
              fontSize: 11.5,
              color: 'var(--text-muted)',
            }}
          >
            Organismo: <strong>{tarjeta.organismo.nombre}</strong> <br />
            Monto Neto: <strong>{formatearCLP(tarjeta.montoNeto)}</strong>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar} disabled={mutacionEliminar.isPending}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={ejecutarEliminacion}
            disabled={mutacionEliminar.isPending}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconoBasura size={14} />
            <span>{mutacionEliminar.isPending ? 'Eliminando…' : 'Sí, Eliminar Oferta'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
